import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CAAS_DRUGTEST_ALLOWED_COUNTRY,
  DRUGTEST_ALLOWED_COUNTRY,
  MEDICAL_CHECK_ALLOWED_COUNTRY,
  SUPPORTED_COUNTRY,
  FeatFlag,
} from "../config/appConfigConstants";
import {
  getRtwAppointmentInfo,
  getRtwTaskDefinition,
  isEligibleForWithdraw,
  isEligibleToDisplayGratitudeComponent,
  isUkRtwNoCaasIntegration,
} from "../helpers/configHelper";
import { isDragonstone, isSalesforceApplication } from "../helpers/url-params";
import { isAirJob } from "../helpers/utils";
import {
  useGetAppConfigQuery,
  useGetBgcStatusQuery,
  useGetCaasInfoQuery,
  useGetMcStatusQuery,
  useGetAllContingenciesByApplicationIdQuery,
  useUpdateCaasI9Mutation,
} from "../reduxStore/api/apiSlice";
import { useAppDispatch } from "../reduxStore/reduxHooks";
import { setConfigData, setFeatureFlag } from "../reduxStore/slices/configSlice";
import { addPageLoadMetric } from "../utility/adobe-analytics";
import {
  getASHConfigurations,
  isCustomerService,
  isDisableCaasI9GETCallEnabledByAppConfig,
  isTaskEnabledByAppConfig,
} from "../utility/app-config-helper";
import { JobDetailsModel, RightToWorkResponse } from "../utility/application-data";
import { dispatchOnDev } from "../utility/dev-env-helpers";
import { CONFIG_DATA_KEY, CONFIG_FEATURE_FLAG } from "../utility/enums/common";
import { getCountryForDomain } from "../utility/locale-helper";
import { useApplicationState } from "./useApplicationState.hook";
import { GratitudeStateType, useGratitudeComponentState } from "./useGratitudeComponentState.hook";
import { AllContingencies, CAAS_STATUS } from "@amzn/hvh-simple-hire-checklist-internal-api-common";
import { useGetJobDetails } from "./apis/useGetJobDetails";
import { useGetCaasRTWStatus } from "./apis/useGetCaasRTWStatus";
import { useGetApplicationManageData } from "./apis/useGetApplicationManageData";
import { useGetNHEAppointment } from "./apis/useGetNHEAppointment";
import { useGetRTWAppointment } from "./apis/useGetRTWAppointment";
import { useGetCandidateData } from "./apis/useGetCandidateData";
import { useGetCandidateAtoZWithoutPassword } from "./apis/useGetCandidateAtoZWithoutPassword";
import { useGetCandidateAtoZWithPassword } from "./apis/useGetCandidateAtoZWithPassword";
import { useGetOnbaseDocumentStatus } from "./apis/useGetOnbaseDocumentStatus";
import { useGetDrugTestStatus } from "./apis/useGetDrugTestStatus";

export const useConfig = () => {
  const [appConfigLoaded, setAppConfigLoaded] = useState(false);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [isErrorLoadingConfig, setIsErrorLoadingConfig] = useState(false);
  const dispatch = useAppDispatch();
  const { applicationId, requisitionId } = useParams<{
    applicationId: string;
    requisitionId: string;
  }>();

  const country = getCountryForDomain();
  const appConfig = useGetAppConfigQuery();
  const candidateData = useGetCandidateData();
  const caasRTW = useGetCaasRTWStatus();
  const scheduleDetails = useGetJobDetails();
  const applicationManageData = useGetApplicationManageData();
  const nheAppointment = useGetNHEAppointment();
  const rtwAppointment = useGetRTWAppointment();
  const applicationState = useApplicationState();
  const candidateAtoZ = useGetCandidateAtoZWithoutPassword();
  const candidateAtoZWithPassword = useGetCandidateAtoZWithPassword();
  const gratitudeComponentState = useGratitudeComponentState(applicationState, nheAppointment, scheduleDetails);

  const isAllContingenciesCallEnabledByAppConfig: boolean =
    isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_AIR_BGC, applicationId) ||
    isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_AIR_DT, applicationId) ||
    isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_BGC, applicationId) ||
    isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_DT, applicationId) ||
    isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_MEDICAL_CHECK, applicationId) ||
    isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_RTW, applicationId) ||
    isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_WORK_AUTH, applicationId);

  // CaaS cannot handle applications without schedule ID
  const validStateForCallingAllContingencies: boolean = Boolean(
    isAllContingenciesCallEnabledByAppConfig && !!applicationManageData?.scheduleId
  );

  const [updateCaasI9] = useUpdateCaasI9Mutation();

  const allContingencies = useGetAllContingenciesByApplicationIdQuery(
    candidateData?.candidateId && validStateForCallingAllContingencies
      ? {
          applicationId,
          candidateId: candidateData?.candidateId,
          redirectUrl: encodeURIComponent(window.location.href),
        }
      : skipToken,
    {
      skip: isSalesforceApplication(applicationId),
    }
  );

  const bgcResponse = useGetBgcStatusQuery(
    {
      candidateId: candidateData?.candidateId,
      applicationId,
      isCustomerService: scheduleDetails?.requisitionCategory === "Customer Service",
    },
    {
      skip: !scheduleDetails,
    }
  );

  const airBgcResponse = useGetBgcStatusQuery(
    {
      candidateId: candidateData?.candidateId,
      applicationId,
      businessLine: scheduleDetails?.businessLine,
      isCustomerService: scheduleDetails?.requisitionCategory === "Customer Service",
    },
    {
      skip: !isAirJob(scheduleDetails?.businessLine),
    }
  );

  const dtResponse = useGetDrugTestStatus();
  const onbaseResponse = useGetOnbaseDocumentStatus();

  const caasI9Response = useGetCaasInfoQuery(
    {
      candidateId: candidateData?.candidateId as string,
      redirectUrl: encodeURIComponent(window.location.href),
    },
    {
      skip:
        appConfig.isLoading ||
        isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_WORK_AUTH, applicationId) ||
        !(country in CAAS_DRUGTEST_ALLOWED_COUNTRY && candidateData),
    }
  );

  const mcResponse = useGetMcStatusQuery(
    {
      candidateId: candidateData?.candidateId,
    },
    {
      skip: !(country in MEDICAL_CHECK_ALLOWED_COUNTRY) || isSalesforceApplication(applicationId),
    }
  );

  const isAppConfigLoaded =
    !appConfig.isLoading &&
    applicationManageData &&
    candidateData &&
    !allContingencies.isLoading &&
    caasRTW !== null && // initial state for the value is null.
    scheduleDetails &&
    !bgcResponse.isLoading &&
    !mcResponse.isLoading &&
    !airBgcResponse.isLoading &&
    onbaseResponse &&
    !caasI9Response.isLoading;

  const getAllContingenciesStatus = () => {
    if (allContingencies.data) {
      dispatch(
        setConfigData({
          key: CONFIG_DATA_KEY.ALL_CONTINGENCIES_STATUS,
          value: allContingencies.data as AllContingencies,
        })
      );
    }
  };

  const getGratitudeComponentUIState = (gratitudeComponentState: GratitudeStateType) => {
    const country = getCountryForDomain();

    dispatch(
      setFeatureFlag({
        key: CONFIG_FEATURE_FLAG.IS_ELIGIBLE_TO_SHOW_GRATITUDE_COMPONENT,
        value: isEligibleToDisplayGratitudeComponent(country, gratitudeComponentState, applicationId),
      })
    );

    dispatch(setConfigData({ key: CONFIG_DATA_KEY.GRATITUDE_COMPONENT_STATE, value: gratitudeComponentState }));
  };

  const getRtwAppointment = () => {
    if (
      isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_RTW, applicationId) &&
      !(isCustomerService(applicationId) || country !== SUPPORTED_COUNTRY.GB)
    ) {
      if (allContingencies.data?.RightToWork) {
        dispatch(
          setConfigData({
            key: CONFIG_DATA_KEY.RTW_APPOINTMENT_INFO,
            value: getRtwAppointmentInfo(country, allContingencies.data.RightToWork as RightToWorkResponse),
          })
        );
      }
    }
  };

  const getIsSalesforceApplication = () => {
    if (isSalesforceApplication(applicationId)) {
      dispatch(
        setFeatureFlag({
          key: CONFIG_FEATURE_FLAG.IS_SALESFORCE_APPLICATION,
          value: isSalesforceApplication(applicationId),
        })
      );
    }
  };

  const getIsEligibleForWithdraw = () => {
    if (applicationManageData) {
      dispatch(
        setFeatureFlag({
          key: CONFIG_FEATURE_FLAG.IS_ELIGIBLE_FOR_WITHDRAW,
          value: isEligibleForWithdraw(applicationId, applicationManageData),
        })
      );
    }
  };

  const getIsUkRtwNoCaasIntegration = () => {
    const rtwTaskDefinition = getRtwTaskDefinition(applicationId);

    dispatch(
      setConfigData({
        key: CONFIG_DATA_KEY.RTW_TASK_DEFINITION,
        value: rtwTaskDefinition,
      })
    );

    dispatch(
      setFeatureFlag({
        key: CONFIG_FEATURE_FLAG.IS_UK_RTW_NO_CAAS_INTEGRATION,
        value: isUkRtwNoCaasIntegration(applicationId),
      })
    );
  };

  const getIsCustomerServiceJob = () => {
    dispatch(
      setFeatureFlag({
        key: CONFIG_FEATURE_FLAG.IS_CUSTOMER_SERVICE_JOB,
        value: isCustomerService(applicationId),
      })
    );
  };

  const getDrugtestStatus = () => {
    if (
      isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_DT, applicationId) &&
      !isSalesforceApplication(applicationId) &&
      !isCustomerService(applicationId)
    ) {
      if (allContingencies.data && scheduleDetails?.businessLine && country in DRUGTEST_ALLOWED_COUNTRY) {
        dispatch(
          setConfigData({
            key: CONFIG_DATA_KEY.DRUG_TEST_STATUS,
            value: isAirJob(scheduleDetails?.businessLine)
              ? allContingencies.data?.AirDrugTest
              : allContingencies.data?.DrugTest,
          })
        );
      }
    }
  };

  const getCaasI9Status = () => {
    const flagEnabled = isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_WORK_AUTH, applicationId);
    if (flagEnabled && allContingencies.data?.WorkAuthorization) {
      if (
        allContingencies.data?.WorkAuthorization.status === CAAS_STATUS.WaitingForASHToCreateOrder &&
        candidateData?.candidateId &&
        !isDisableCaasI9GETCallEnabledByAppConfig()
      ) {
        updateCaasI9({
          candidateId: candidateData?.candidateId,
          redirectURL: encodeURIComponent(window.location.href),
        })
          .unwrap()
          .then((i9Response) => {
            dispatch(
              setConfigData({
                key: CONFIG_DATA_KEY.CAAS_I9_STATUS,
                value: i9Response,
              })
            );
          });
      } else {
        dispatch(
          setConfigData({
            key: CONFIG_DATA_KEY.CAAS_I9_STATUS,
            value: allContingencies.data.WorkAuthorization,
          })
        );
      }
    } else if (!flagEnabled || (isSalesforceApplication(applicationId) && caasI9Response.data)) {
      dispatch(
        setConfigData({
          key: CONFIG_DATA_KEY.CAAS_I9_STATUS,
          value: caasI9Response.data,
        })
      );
    }
  };

  const getCaasRTWStatus = () => {
    const flagEnabled = isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_RTW, applicationId);
    if (flagEnabled && allContingencies.data?.RightToWork) {
      dispatch(
        setConfigData({
          key: CONFIG_DATA_KEY.CAAS_RTW_STATUS,
          value: allContingencies.data.RightToWork,
        })
      );
    } else if (!flagEnabled && caasRTW) {
      dispatch(
        setConfigData({
          key: CONFIG_DATA_KEY.CAAS_RTW_STATUS,
          value: caasRTW,
        })
      );
    }
  };

  const getMedicalCheckStatus = () => {
    const flagEnabled = isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_MEDICAL_CHECK, applicationId);

    if (flagEnabled) {
      if (
        allContingencies.data?.MedicalCheck &&
        country in MEDICAL_CHECK_ALLOWED_COUNTRY &&
        !isSalesforceApplication(applicationId)
      ) {
        dispatch(
          setConfigData({
            key: CONFIG_DATA_KEY.MEDICAL_CHECK_STATUS,
            value: allContingencies.data.MedicalCheck,
          })
        );
      }
    } else if (!flagEnabled && mcResponse.data) {
      dispatch(
        setConfigData({
          key: CONFIG_DATA_KEY.MEDICAL_CHECK_STATUS,
          value: mcResponse.data,
        })
      );
    }
  };

  const getBackgroundCheckStatus = () => {
    const flagEnabled = isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_BGC, applicationId);
    if (!isAirJob(scheduleDetails?.businessLine)) {
      if (flagEnabled && !isSalesforceApplication(applicationId) && allContingencies.data) {
        dispatch(
          setConfigData({
            key: CONFIG_DATA_KEY.BACKGROUND_CHECK_STATUS,
            value: allContingencies.data.BGC,
          })
        );
      } else if ((!flagEnabled || isSalesforceApplication(applicationId)) && bgcResponse.data) {
        dispatch(
          setConfigData({
            key: CONFIG_DATA_KEY.BACKGROUND_CHECK_STATUS,
            value: bgcResponse.data,
          })
        );
      }
    }
  };

  const getAirBackgroundCheckStatus = () => {
    if (isAirJob(scheduleDetails?.businessLine)) {
      const flagEnabled = isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_AIR_BGC, applicationId);
      if (flagEnabled && allContingencies.data?.AirBGC) {
        dispatch(
          setConfigData({
            key: CONFIG_DATA_KEY.AIR_BACKGROUND_CHECK_STATUS,
            value: allContingencies.data.AirBGC,
          })
        );
      } else if (!flagEnabled && airBgcResponse.data) {
        dispatch(
          setConfigData({
            key: CONFIG_DATA_KEY.AIR_BACKGROUND_CHECK_STATUS,
            value: airBgcResponse.data,
          })
        );
      }
    }
  };

  const checkForError = () => {
    if (appConfig.isError) {
      dispatchOnDev(() => console.error("error loading appConfig: ", appConfig.error));
      setIsErrorLoadingConfig(true);
    }
  };

  const setupASHConfigs = async (scheduleDetails: JobDetailsModel, appConfig: any) => {
    if (!appConfigLoaded) {
      const configResponse = await getASHConfigurations(applicationId, scheduleDetails, appConfig);

      if (configResponse?.successfullyLoaded) {
        dispatch(
          setFeatureFlag({
            key: CONFIG_FEATURE_FLAG.IS_ASH_CONFIG_LOADED,
            value: configResponse.successfullyLoaded,
          })
        );
        setAppConfigLoaded(configResponse.successfullyLoaded);
      }
    }
  };

  useEffect(() => {
    if (appConfigLoaded && isAppConfigLoaded) {
      getIsCustomerServiceJob();
      getIsUkRtwNoCaasIntegration();
      getIsEligibleForWithdraw();
      getRtwAppointment();
      getAllContingenciesStatus();
      getDrugtestStatus();
      getMedicalCheckStatus();
      getAirBackgroundCheckStatus();
      getBackgroundCheckStatus();
      getCaasI9Status();
      getCaasRTWStatus();
      getIsSalesforceApplication();
      setIsConfigLoaded(true);
    }
  }, [isAppConfigLoaded, appConfigLoaded]);

  useEffect(() => {
    getGratitudeComponentUIState(gratitudeComponentState);
  }, [isAppConfigLoaded, gratitudeComponentState, appConfigLoaded]);

  // Calling ASH configuration from AppConfig
  useEffect(() => {
    if (
      !appConfigLoaded &&
      applicationId &&
      requisitionId &&
      scheduleDetails &&
      appConfig.data &&
      candidateData &&
      applicationManageData &&
      scheduleDetails
    ) {
      setupASHConfigs(scheduleDetails, appConfig.data);
      // TODO: Refactor global variable to use redux instead
      window.ASH_CHECKLIST_isChecklistReferral = true;
      addPageLoadMetric(candidateData, applicationManageData, scheduleDetails, window, applicationId, "Ash thank you", {
        jobId: requisitionId,
        isDragonstone: isDragonstone(requisitionId),
      });
    }
  }, [
    appConfigLoaded,
    applicationId,
    requisitionId,
    appConfig,
    scheduleDetails,
    appConfig.data,
    applicationManageData,
    candidateData,
    scheduleDetails,
  ]);

  checkForError();

  return { isConfigLoaded, isErrorLoadingConfig, appConfigLoaded };
};
